import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import ModalOverlay from './ModalOverlay'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TEXTAREA = 'w-full resize-y rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TH = 'text-secondary border-b border-secondary px-2.5 py-1.5 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-1.5 align-middle'
const LABEL = 'text-secondary text-[11px] font-medium'
const FIELD = 'flex flex-col gap-1'

export default function AIPurchaseAssistAbTests() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()
  const [tests, setTests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [showCreate, setShowCreate] = useState(false)

  const fetchAll = async () => {
    setLoading(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ab-tests`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setTests(d.items || [])
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { fetchAll() }, [siteId])

  const STATUS_BADGE = {
    draft: { label: '📝 下書き', cls: 'bg-surface-warning text-warning' },
    running: { label: '🟢 実行中', cls: 'bg-surface-success text-success' },
    completed: { label: '✓ 完了', cls: 'bg-surface-emphasis text-emphasis' },
    aborted: { label: '⛔ 中断', cls: 'bg-surface-critical text-critical' },
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='🧪 A/Bテスト管理'
        description="Findee配信のA/Bテスト。カイ二乗検定 + Cohen's h + 早期停止判定（補足資料準拠）。"
        rightNode={
          <div className='flex flex-wrap items-center gap-1.5'>
            <Button color={ButtonColor.PRIMARY} onClick={() => setShowCreate(true)}>+ 新規テスト作成</Button>
            <Button color={ButtonColor.SECONDARY} onClick={fetchAll}>🔄 更新</Button>
          </div>
        }
      />

      {error && (
        <div className='bg-surface-critical text-critical mb-2.5 rounded-lg p-2.5 text-xs'>エラー: {error}</div>
      )}

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : tests.length === 0 ? (
        <div className={`${CARD} text-tertiary p-8 text-center text-xs`}>
          A/Bテストがありません。「+ 新規テスト作成」から開始してください。
        </div>
      ) : (
        <div className={`${CARD} overflow-x-auto`}>
          <table className='w-full border-collapse text-xs'>
            <thead>
              <tr className='bg-surface-secondary'>
                <th className={TH}>ID</th>
                <th className={TH}>テスト名</th>
                <th className={TH}>種別</th>
                <th className={TH}>分割</th>
                <th className={TH}>ステータス</th>
                <th className={TH}>開始日</th>
                <th className={TH}>操作</th>
              </tr>
            </thead>
            <tbody>
              {tests.map(t => {
                const sb = STATUS_BADGE[t.status] || STATUS_BADGE.draft
                const typeLabel = t.test_type === 'on_off' ? 'ON/OFF' : t.test_type === 'campaign_vs_campaign' ? '配信A vs B' : '単一要素'
                let summary = ''
                let retired = null
                if (t.test_type === 'campaign_vs_campaign') {
                  summary = `配信#${t.campaign_id} ⇔ #${t.campaign_b_id || '?'}`
                } else if (t.test_type === 'single_element') {
                  const ovA = (t.variant_a_config && t.variant_a_config.overrides) || {}
                  const ovB = (t.variant_b_config && t.variant_b_config.overrides) || {}
                  const k = Object.keys(ovA)[0] || Object.keys(ovB)[0]
                  if (k) summary = `${k}: ${formatBrief(ovA[k])} vs ${formatBrief(ovB[k])}`
                  // 廃止した比較対象を使っているテストは、黙って効かなくなる状態にしない
                  retired = k ? RETIRED_SINGLE_ELEMENT_FIELDS[k] : null
                } else if (t.test_type === 'on_off') {
                  summary = `配信#${t.campaign_id} 表示 vs 非表示`
                }
                return (
                  <tr key={t.id} className='border-b border-secondary'>
                    <td className={`${TD} text-tertiary font-mono`}>#{t.id}</td>
                    <td className={`${TD} font-semibold`}>
                      {t.name}
                      {summary && <div className='text-tertiary mt-0.5 text-[10px] font-normal'>{summary}</div>}
                      {retired && (
                        <div className='bg-surface-critical text-critical mt-1 rounded p-1.5 text-[10px] font-normal'>
                          ⚠ 比較対象「{retired.label}」は廃止されました。{retired.reason}
                          <br />{retired.alternative}
                        </div>
                      )}
                    </td>
                    <td className={`${TD} text-secondary text-[10px]`}>{typeLabel}</td>
                    <td className={`${TD} text-[10px]`}>A:{t.split_ratio_a} / B:{100 - t.split_ratio_a}</td>
                    <td className={TD}>
                      <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${sb.cls}`}>{sb.label}</span>
                    </td>
                    <td className={`${TD} text-tertiary text-[10px]`}>{t.started_at ? new Date(t.started_at).toLocaleDateString('ja-JP') : '-'}</td>
                    <td className={TD}><Button color={ButtonColor.SECONDARY} onClick={() => setSelectedId(t.id)}>📊 詳細</Button></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && <TestDetail siteId={siteId} testId={selectedId} authHeaders={authHeaders} onClose={() => setSelectedId(null)} onChanged={fetchAll} />}
      {showCreate && <CreateTest siteId={siteId} authHeaders={authHeaders} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchAll() }} />}
    </Main>
  )
}

function TestDetail({ siteId, testId, authHeaders, onClose, onChanged }) {
  const [analysis, setAnalysis] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchAnalysis = async () => {
    setLoading(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ab-tests/${testId}/analyze`, { headers: authHeaders() })
      if (r.ok) setAnalysis(await r.json())
    } finally { setLoading(false) }
  }
  useEffect(() => { fetchAnalysis() }, [testId])

  const updateStatus = async (status) => {
    if (!window.confirm(`このテストを「${status}」に変更します。よろしいですか？`)) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/ab-tests/${testId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ status }),
    })
    if (r.ok) { fetchAnalysis(); onChanged() }
    else alert('更新失敗')
  }

  const deleteTest = async () => {
    if (!window.confirm('このテストを削除します。よろしいですか？')) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/ab-tests/${testId}`, { method: 'DELETE', headers: authHeaders() })
    if (r.ok) { onClose(); onChanged() }
    else alert('削除失敗')
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-start justify-center bg-black/60 px-4 pt-8 pb-20'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} max-h-[90vh] w-full max-w-3xl overflow-auto p-5`}>
        <div className='mb-3.5 flex items-center justify-between'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>📊 A/Bテスト #{testId} 分析結果</h3>
          <div className='flex gap-1.5'>
            <Button color={ButtonColor.SECONDARY} onClick={fetchAnalysis}>🔄</Button>
            <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
          </div>
        </div>

        {loading || !analysis ? (
          <div className='text-secondary py-16 text-center text-sm'>分析中...</div>
        ) : (
          <>
            {/* テスト情報 */}
            <div className='mb-3.5'>
              <h4 className='text-primary m-0 mb-1 text-[13px] font-bold'>{analysis.test_name}</h4>
              <div className='text-secondary text-[11px]'>status: {analysis.status} / 検定手法: {analysis.test_method} / α={analysis.alpha}</div>
            </div>

            {/* CVR比較 */}
            <div className='mb-3.5 grid grid-cols-2 gap-3'>
              <VariantCard label="A群" n={analysis.n_a} purchase={analysis.purchase_a} cvr={analysis.cvr_a} accentVar="--color-fill-success" labelCls="text-success" />
              <VariantCard label="B群" n={analysis.n_b} purchase={analysis.purchase_b} cvr={analysis.cvr_b} accentVar="--color-fill-brand" labelCls="text-emphasis" />
            </div>

            {/* 統計判定 */}
            <div className={`${CARD} bg-surface-emphasis mb-3.5 p-3.5`}>
              <h4 className='text-emphasis m-0 mb-2 text-xs font-bold'>📐 統計判定</h4>
              <div className='grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2.5 text-[11px]'>
                <Stat label="差分(B-A)" value={`${analysis.diff_pct >= 0 ? '+' : ''}${analysis.diff_pct.toFixed(2)}%`} valueCls={analysis.diff_pct > 0 ? 'text-success' : 'text-critical'} />
                <Stat label="カイ二乗値" value={analysis.chi2 !== null ? analysis.chi2.toFixed(3) : 'N/A'} />
                <Stat label="p値" value={analysis.p_value !== null ? analysis.p_value.toFixed(4) : 'N/A'} valueCls={analysis.significant_005 ? 'text-success' : 'text-secondary'} />
                <Stat label="効果量 Cohen's h" value={`${analysis.cohen_h.toFixed(3)} (${analysis.effect_label})`} />
                <Stat label="α=0.05 有意" value={analysis.significant_005 ? '✓ 有意' : '✗ 非有意'} valueCls={analysis.significant_005 ? 'text-success' : 'text-critical'} />
                <Stat label="α=0.01 有意" value={analysis.significant_001 ? '✓ 高度に有意' : '✗ 非有意'} valueCls={analysis.significant_001 ? 'text-success' : 'text-tertiary'} />
                <Stat label="サンプル充足" value={analysis.sample_size_ok ? '✓ OK' : `✗ 各群 ${analysis.min_sample_required}+ 必要`} valueCls={analysis.sample_size_ok ? 'text-success' : 'text-warning'} />
              </div>
            </div>

            {/* 早期停止モニター（プログレスバー） */}
            <EarlyStopMonitor analysis={analysis} />

            {/* 早期停止判定 */}
            {analysis.early_stop_recommended && (
              <div className='bg-surface-critical mb-3.5 rounded-lg border-l-4 p-3' style={{ borderLeftColor: 'var(--color-fill-critical)' }}>
                <div className='text-critical mb-1 text-xs font-bold'>⚠️ 早期停止推奨</div>
                <div className='text-secondary text-[11px]'>{analysis.early_stop_reason}</div>
              </div>
            )}

            {/* 推奨アクション */}
            <div className='bg-surface-emphasis mb-3.5 rounded-lg border-l-4 p-3' style={{ borderLeftColor: 'var(--color-fill-brand)' }}>
              <div className='text-emphasis mb-1 text-xs font-bold'>💡 推奨アクション</div>
              <div className='text-primary text-xs'>{analysis.recommendation}</div>
            </div>

            {/* ステータス操作 */}
            <div className='flex flex-wrap justify-between gap-1.5'>
              <div className='flex gap-1.5'>
                {analysis.status === 'draft' && <Button color={ButtonColor.PRIMARY} onClick={() => updateStatus('running')}>🟢 実行開始</Button>}
                {analysis.status === 'running' && <Button color={ButtonColor.PRIMARY} onClick={() => updateStatus('completed')}>✓ 完了にする</Button>}
                {analysis.status === 'running' && <Button color={ButtonColor.SECONDARY} onClick={() => updateStatus('aborted')}>⛔ 中断</Button>}
              </div>
              <Button color={ButtonColor.CRITICAL} onClick={deleteTest}>🗑 削除</Button>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  )
}

// single_element の比較対象フィールド定義
const SINGLE_ELEMENT_FIELDS = [
  { key: 'ai_profile_id', label: 'AIプロファイル', type: 'profile' },
  { key: 'rich_ui_enabled', label: 'リッチUI ON/OFF', type: 'boolean' },
  { key: 'rich_ui_theme.accent_color', label: 'アクセント色（リッチUI）', type: 'color' },
  { key: 'design_config.position', label: 'ウィジェット位置', type: 'select', options: [{ value: 'br', label: '右下' }, { value: 'bl', label: '左下' }, { value: 'center', label: '中央下' }] },
  { key: 'design_config.main_color', label: 'メインカラー（ヘッダー）', type: 'color' },
  { key: 'trigger_config.auto_expand', label: '自動展開', type: 'boolean' },
  { key: 'trigger_config.delay_sec', label: '自動展開までの遅延秒数', type: 'number' },
]

// 廃止した比較対象。既存テストが参照している場合に無効である旨を示すために保持する
const RETIRED_SINGLE_ELEMENT_FIELDS = {
  'design_config.initial_message': {
    label: '初期メッセージ',
    reason: '開始メッセージはAIプロファイルへ移動したため、配信単位での上書きは行われません。',
    alternative: 'AIプロファイルを複製して開始メッセージだけを変え、比較対象「AIプロファイル」でテストしてください。',
  },
}

function CreateTest({ siteId, authHeaders, onClose, onCreated }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [testType, setTestType] = useState('on_off')
  const [splitRatio, setSplitRatio] = useState(50)
  const [segmentation, setSegmentation] = useState('random')
  const [campaignId, setCampaignId] = useState('')
  const [campaignBId, setCampaignBId] = useState('')
  const [campaigns, setCampaigns] = useState([])
  const [profiles, setProfiles] = useState([])
  const [saving, setSaving] = useState(false)
  // single_element 用
  const [seField, setSeField] = useState('ai_profile_id')
  const [seValueA, setSeValueA] = useState('')
  const [seValueB, setSeValueB] = useState('')

  useEffect(() => {
    fetch(`/api/sites/${siteId}/purchase-assist/campaigns`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setCampaigns(d.items || []))
    fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { items: [] }).then(d => setProfiles(d.items || []))
  }, [])

  const fieldDef = SINGLE_ELEMENT_FIELDS.find(f => f.key === seField)

  const submit = async () => {
    if (!name.trim()) { alert('テスト名を入力してください'); return }
    if (!campaignId) { alert('対象配信を選択してください'); return }
    if (testType === 'campaign_vs_campaign') {
      if (!campaignBId) { alert('配信Bを選択してください'); return }
      if (campaignBId === campaignId) { alert('配信Aと配信Bは異なるものを選んでください'); return }
    }

    // variant_*_config を test_type ごとに組み立て
    let variant_a_config = {}
    let variant_b_config = {}
    if (testType === 'single_element') {
      if (!seField) { alert('比較する要素を選んでください'); return }
      // boolean は 'false' / 'true' どちらも有効値のため空チェック対象外
      if (fieldDef?.type !== 'boolean') {
        if (seValueA === '' || seValueA == null) { alert('A値を入力してください'); return }
        if (seValueB === '' || seValueB == null) { alert('B値を入力してください'); return }
      }
      const av = parseValueByType(seValueA, fieldDef?.type)
      const bv = parseValueByType(seValueB, fieldDef?.type)
      variant_a_config = { overrides: { [seField]: av } }
      variant_b_config = { overrides: { [seField]: bv } }
    }

    setSaving(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ab-tests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          name: name.trim(),
          description,
          test_type: testType,
          campaign_id: campaignId ? parseInt(campaignId) : null,
          campaign_b_id: testType === 'campaign_vs_campaign' && campaignBId ? parseInt(campaignBId) : null,
          variant_a_config,
          variant_b_config,
          split_ratio_a: splitRatio,
          segmentation,
        }),
      })
      if (!r.ok) throw new Error((await r.json()).detail || r.status)
      onCreated()
    } catch (e) {
      alert(`作成失敗: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-center justify-center bg-black/60 px-4 py-8'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} max-h-[90vh] w-full max-w-xl overflow-y-auto p-5`}>
        <div className='mb-3.5 flex items-center justify-between'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>+ 新規A/Bテスト</h3>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
        </div>
        <div className='flex flex-col gap-2.5'>
          <Field label="テスト名" required>
            <input value={name} onChange={e => setName(e.target.value)} className={INPUT} placeholder="例: ウィジェット位置 右下 vs 左下" />
          </Field>
          <Field label="説明">
            <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} className={TEXTAREA} />
          </Field>
          <Field label="テスト種別">
            <select value={testType} onChange={e => setTestType(e.target.value)} className={INPUT}>
              <option value="on_off">Findee ON/OFF（導入効果証明）</option>
              <option value="campaign_vs_campaign">配信A vs 配信B</option>
              <option value="single_element">単一要素A/B（プロンプト/位置/メッセージ等）</option>
            </select>
            <div className='text-tertiary mt-1 text-[10px]'>
              {testType === 'on_off' && 'A群=ウィジェット表示、B群=非表示。導入効果（接触有無のCVR差）を計測'}
              {testType === 'campaign_vs_campaign' && '配信を2つ選んで比較。各配信のリッチUI設定/プロンプト等が丸ごと違ってOK'}
              {testType === 'single_element' && '配信内の特定要素（メッセージ・色・プロンプト等）だけを A/B で比較'}
            </div>
          </Field>

          {/* test_type 別の追加入力 */}
          {testType === 'on_off' && (
            <Field label="対象配信" hint="A群はこの配信を受ける、B群は非表示">
              <select value={campaignId} onChange={e => setCampaignId(e.target.value)} className={INPUT}>
                <option value="">— 未選択 —</option>
                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}（{c.status}）</option>)}
              </select>
            </Field>
          )}

          {testType === 'campaign_vs_campaign' && (
            <>
              <Field label="A群: 配信A" hint="A群が見る配信">
                <select value={campaignId} onChange={e => setCampaignId(e.target.value)} className={INPUT}>
                  <option value="">— 未選択 —</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}（{c.status}）</option>)}
                </select>
              </Field>
              <Field label="B群: 配信B" hint="B群が見る配信（A と異なるもの）">
                <select value={campaignBId} onChange={e => setCampaignBId(e.target.value)} className={INPUT}>
                  <option value="">— 未選択 —</option>
                  {campaigns.filter(c => String(c.id) !== campaignId).map(c => <option key={c.id} value={c.id}>{c.name}（{c.status}）</option>)}
                </select>
              </Field>
            </>
          )}

          {testType === 'single_element' && (
            <>
              <Field label="対象配信" hint="A群もB群も同じ配信を受け、指定要素だけを切り替え">
                <select value={campaignId} onChange={e => setCampaignId(e.target.value)} className={INPUT}>
                  <option value="">— 未選択 —</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}（{c.status}）</option>)}
                </select>
              </Field>
              <Field label="比較する要素">
                <select value={seField} onChange={e => { setSeField(e.target.value); setSeValueA(''); setSeValueB('') }} className={INPUT}>
                  {SINGLE_ELEMENT_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </Field>
              {seField === 'ai_profile_id' && (
                <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
                  💡 開始メッセージや質問例を比較したい場合は、<strong>AIプロファイルを複製して該当項目だけを変え</strong>、
                  ここでその2つを比較してください（開始メッセージはAIプロファイル側の設定です）。
                </div>
              )}
              <div className='grid grid-cols-2 gap-2.5'>
                <Field label="A値">
                  <SingleElementValueInput field={fieldDef} value={seValueA} onChange={setSeValueA} profiles={profiles} />
                </Field>
                <Field label="B値">
                  <SingleElementValueInput field={fieldDef} value={seValueB} onChange={setSeValueB} profiles={profiles} />
                </Field>
              </div>
            </>
          )}

          <Field label={`A群分割比率 (${splitRatio}%) / B群 (${100 - splitRatio}%)`}>
            <input type="range" min={10} max={90} step={5} value={splitRatio} onChange={e => setSplitRatio(parseInt(e.target.value))} className='w-full' />
          </Field>
          <Field label="セグメント分割">
            <select value={segmentation} onChange={e => setSegmentation(e.target.value)} className={INPUT}>
              <option value="random">ランダム</option>
              <option value="device">デバイス別</option>
              <option value="new_existing">新規/既存</option>
              <option value="member_status">会員/非会員</option>
            </select>
          </Field>
        </div>
        <div className='mt-3.5 flex justify-end gap-1.5'>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>取消</Button>
          <Button color={ButtonColor.PRIMARY} onClick={submit} disabled={saving} loading={saving}>{saving ? '作成中...' : '+ 作成'}</Button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// 比較する要素の値入力（型に応じてUI切替）
function SingleElementValueInput({ field, value, onChange, profiles }) {
  if (!field) return <input value={value} onChange={e => onChange(e.target.value)} className={INPUT} />
  if (field.type === 'profile') {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className={INPUT}>
        <option value="">— 未選択 —</option>
        {(profiles || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    )
  }
  if (field.type === 'boolean') {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className={INPUT}>
        <option value="">— 選択 —</option>
        <option value="true">ON (true)</option>
        <option value="false">OFF (false)</option>
      </select>
    )
  }
  if (field.type === 'select') {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className={INPUT}>
        <option value="">— 選択 —</option>
        {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    )
  }
  if (field.type === 'color') {
    return (
      <div className='flex items-center gap-1'>
        <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} className='h-8 w-8 cursor-pointer rounded-lg border border-border-input bg-surface' />
        <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} className={`${INPUT} font-mono`} placeholder="例: 6366f1" />
      </div>
    )
  }
  if (field.type === 'number') {
    return <input type="number" value={value} onChange={e => onChange(e.target.value)} className={INPUT} />
  }
  return <input value={value} onChange={e => onChange(e.target.value)} className={INPUT} />
}

function parseValueByType(raw, type) {
  if (type === 'boolean') return raw === 'true'
  if (type === 'number') return raw === '' || raw == null ? null : Number(raw)
  if (type === 'profile') return raw === '' || raw == null ? null : parseInt(raw)
  return raw
}

function formatBrief(v) {
  if (v === null || v === undefined) return '-'
  if (typeof v === 'boolean') return v ? 'ON' : 'OFF'
  const s = String(v)
  return s.length > 18 ? s.slice(0, 18) + '…' : s
}

function VariantCard({ label, n, purchase, cvr, accentVar, labelCls }) {
  // 95%信頼区間（比率の正規近似）: p ± 1.96 * sqrt(p(1-p)/n)
  const p = cvr / 100
  const ci = n > 0 ? 1.96 * Math.sqrt(p * (1 - p) / n) : 0
  const ciLow = Math.max(0, (p - ci) * 100)
  const ciHigh = Math.min(100, (p + ci) * 100)
  return (
    <div className={`${CARD} border-l-4 p-3.5`} style={{ borderLeftColor: `var(${accentVar})` }}>
      <div className={`mb-1.5 text-xs font-bold ${labelCls}`}>{label}</div>
      <div className='grid grid-cols-2 gap-1.5 text-[11px]'>
        <Stat label="サンプル数" value={n.toLocaleString()} />
        <Stat label="購買数" value={purchase.toLocaleString()} />
        <Stat label="CVR" value={`${cvr.toFixed(3)}%`} valueCls="text-success" />
        {n > 0 && (
          <Stat label="95%信頼区間" value={`${ciLow.toFixed(2)} - ${ciHigh.toFixed(2)}%`} valueCls="text-emphasis" />
        )}
      </div>
    </div>
  )
}

function EarlyStopMonitor({ analysis }) {
  // 3条件のプログレス表示（補足資料 4.2）
  const cond1 = Math.min(100, Math.abs(analysis.diff_pct) / 50 * 100)  // 効果差50%基準
  const cond2 = (analysis.p_value !== null && analysis.p_value < 0.01) ? 100 :
                (analysis.p_value !== null ? Math.max(0, (1 - analysis.p_value / 0.01) * 100) : 0)
  const minSample = analysis.min_sample_required || 1000
  const cond3 = Math.min(100, Math.min(analysis.n_a, analysis.n_b) / (minSample * 0.5) * 100)
  const conditionRow = (label, pct, ok) => (
    <div className='mb-1.5'>
      <div className='text-secondary mb-0.5 flex justify-between text-[10px]'>
        <span>{ok ? '✓' : '○'} {label}</span>
        <span>{pct.toFixed(0)}/100</span>
      </div>
      <div className='bg-surface-secondary h-1 overflow-hidden rounded-sm'>
        <div className={`h-full transition-[width] duration-300 ${ok ? 'bg-fill-success' : 'bg-fill-warning'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
  return (
    <div className={`${CARD} bg-surface-warning mb-3.5 p-3`}>
      <h4 className='text-warning m-0 mb-2 text-xs font-bold'>🚦 早期停止モニター</h4>
      {conditionRow(`効果差: ${analysis.diff_pct >= 0 ? '+' : ''}${analysis.diff_pct.toFixed(2)}% (基準: ±50%)`, cond1, Math.abs(analysis.diff_pct) >= 50)}
      {conditionRow(`p値: ${analysis.p_value !== null ? analysis.p_value.toFixed(4) : 'N/A'} (基準: <0.01)`, cond2, analysis.significant_001)}
      {conditionRow(`サンプル: A=${analysis.n_a}, B=${analysis.n_b} (基準: 各${(minSample * 0.5).toFixed(0)}+)`, cond3, Math.min(analysis.n_a, analysis.n_b) >= minSample * 0.5)}
    </div>
  )
}

function Stat({ label, value, valueCls = 'text-primary' }) {
  return (
    <div className='bg-surface-secondary rounded p-1.5'>
      <div className='text-tertiary text-[9px]'>{label}</div>
      <div className={`mt-0.5 text-[13px] font-bold ${valueCls}`}>{value}</div>
    </div>
  )
}

function Field({ label, hint, children, required }) {
  return (
    <div className={FIELD}>
      <label className={LABEL}>
        {label}{required && <span className='text-critical ml-0.5'>*</span>}
        {hint && <span className='text-tertiary ml-1.5 text-[10px] font-normal'>— {hint}</span>}
      </label>
      {children}
    </div>
  )
}
